import {
    AgeGroup,
    FileKind,
    FileType,
    IdentificationType,
    PersonDetails,
    QuoteDestination,
    TimeOfDayCategory,
    TravelCoverType,
    TravelType,
    UserRole
} from '../models';
import { format } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';
import Kenya from 'assets/images/kenya.png';
import Rwanda from 'assets/images/rwanda.png';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

export const mapFileExtensionToFileType = (fileExtension: string): FileType => {
    switch (fileExtension) {
        case 'application/pdf':
            return FileType.PDF;
        case 'application/doc':
        case 'application/msword':
            return FileType.DOC;
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            return FileType.DOCX;
        case 'application/docx':
            return FileType.DOCX;
        case 'application/pptx':
        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
            return FileType.PPTX;
        case 'image/jpg':
            return FileType.JPG;
        case 'image/jpeg':
            return FileType.JPEG;
        case 'image/png':
            return FileType.PNG;
        case 'text/csv':
            return FileType.CSV;
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            return FileType.EXCEL;
        case 'video/mp4':
            return FileType.VIDEO;
        default:
            return FileType.UNKNOWN;
    }
};

export const mapFileTypeToFileExtension = (fileType: FileType): string => {
    switch (fileType) {
        case FileType.PDF:
            return 'application/pdf';
        case FileType.DOC:
            return 'application/msword';
        case FileType.DOCX:
            return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        case FileType.PPTX:
            return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
        case FileType.JPG:
            return 'image/jpg';
        case FileType.JPEG:
            return 'image/jpeg';
        case FileType.PNG:
            return 'image/png';
        case FileType.CSV:
            return 'text/csv';
        case FileType.EXCEL:
            return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        case FileType.VIDEO:
            return 'video/mp4';
        default:
            return 'application/octet-stream';
    }
};

const extensionToMimeType: Record<string, string> = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    jpg: 'image/jpg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    csv: 'text/csv',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    mp4: 'video/mp4'
};

export const mapExtensionToMimeType = (extension: string): string => {
    return extensionToMimeType[extension.toLowerCase()] || 'application/octet-stream';
};

export const getFileExtension = (fileName: string): string => fileName.split('.').slice(-1)[0];

// export const downloadCSV = (jsonData: any, fileName: string, fields: string[]) => {
//     const csv = Papa.unparse({ data: jsonData, fields: fields });

//     const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
//     const link = document.createElement('a');
//     const url = URL.createObjectURL(blob);
//     link.setAttribute('href', url);
//     link.setAttribute('download', fileName);
//     link.style.visibility = 'hidden';
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
// };

export const addCommasToNumbers = (input: string) => {
    const [integerPart, decimalPart] = input.split('.');
    const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decimalPart ? `${formattedIntegerPart}.${decimalPart}` : formattedIntegerPart;
};

export const FormattedRoleName = (input: string) =>
    input
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());

export const printStringList = (list: string[]): string => {
    if (list.length === 0) {
        return '';
    } else if (list.length === 1) {
        return `${list[0]}`;
    } else if (list.length === 2) {
        return `${list[0]} and ${list[1]}`;
    } else {
        const itemsExceptLast = list
            .slice(0, -1)
            .map((item) => `${item}`)
            .join(', ');
        const lastItem = `${list[list.length - 1]}`;
        return `${itemsExceptLast} and ${lastItem}`;
    }
};

export const createFormattedDateAndTime = (dateTimeString: string) => {
    const [date, time] = dateTimeString.split('T');
    const formattedDate = format(new Date(date), 'MMMM d, yyyy');
    const formattedTime = `${time} hrs`;
    return { formattedDate, formattedTime };
};

export const calculateDurationInDays = (startDate: Dayjs, endDate: Dayjs): number => {
    const differenceInMs = endDate.diff(startDate);
    const days = differenceInMs / (1000 * 3600 * 24);

    return Math.round(days) + 1;
};

export const getUserInitials = (userName: string): string => {
    const splitUserName: string[] = userName.split(' ');
    return (splitUserName[0][0] + splitUserName[1][0]).toUpperCase();
};

export const convertJsonToBlob = (jsonObj: Record<string, any>): Blob => {
    const json = JSON.stringify(jsonObj);

    return new Blob([json], {
        type: 'application/json'
    });
};

export const transformStringToList = (value: string) => value.split(',').map((value) => value.trim());

export const transformListToString = (values: string[]): string => values.join(',');

export const isEndDateBeforeStartDate = (startDate: Dayjs, endDate: Dayjs): boolean => {
    if (!startDate.isValid() || !endDate.isValid()) {
        throw new Error('Invalid date or time format');
    }

    return endDate.isBefore(startDate);
};

export const separateDateAndTime = (dateString: string): { date: string; time: string } => {
    const dateTimeList: string[] = dateString.split('T');
    return {
        date: dateTimeList[0],
        time: dateTimeList[1]
    };
};

export const combineDateAndTime = (date: string, time: string) => dayjs(`${date}T${time}`);

export const convertDateToReadableFormat = (dateString: string): string => {
    const dateObject = dayjs(dateString, 'YYYY-MM-DD');
    return dateObject.format('MMMM D, YYYY');
};

export const convertTimeToReadableFormat = (timeString: string): string => {
    const [hour, minute, _] = timeString.split(':');
    return `${hour}:${minute}`;
};

export const getCurrentTimeOfDayCategory = () => {
    const currentHour = new Date().getHours();

    if (currentHour >= 5 && currentHour < 12) {
        return TimeOfDayCategory.MORNING;
    } else if (currentHour >= 12 && currentHour < 18) {
        return TimeOfDayCategory.AFTERNOON;
    } else {
        return TimeOfDayCategory.EVENING;
    }
};

export const getWordCount = (text: string) => text.split(/\s+/).filter((word) => word.length > 0).length;

//* Function to remove HTML tags
export const removeHTMLTags = (html: string): string => {
    var div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
};

export const cleanPunctuatedText = (text: string): string => {
    return text.replace(/[^.,\w\s:"]/g, '');
};

export const convertBytesToMB = (fileSize: string): string => {
    const bytes: number = Number(fileSize.split(' ')[0]);
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const getCleanFilename = (fileName: string): string => fileName.replace(/^\d+-/, '');

export const formatBenefits = (text: string): string => {
    const cleanedText = text.replace(/[\n]/g, '');
    const items = cleanedText
        .split('•')
        .slice(1)
        .map((item) => item.trim());
    const result = items.join(' ~ ');

    return result;
};

export const downloadBase64File = (base64Data: string, filename: string, mimeType: string) => {
    const link = document.createElement('a');
    link.href = `data:${mimeType};base64,${base64Data}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const getBackgroundColor = (letter: string) => {
    const colorMap: Record<string, string> = {
        A: 'bg-cyan-300',
        B: 'bg-green-300',
        C: 'bg-blue-300',
        D: 'bg-yellow-300',
        E: 'bg-indigo-300',
        F: 'bg-pink-300',
        G: 'bg-purple-300',
        H: 'bg-red-300',
        I: 'bg-teal-300',
        J: 'bg-amber-300',
        K: 'bg-lime-300',
        L: 'bg-cyan-300',
        M: 'bg-red-300',
        N: 'bg-emerald-300',
        O: 'bg-violet-300',
        P: 'bg-fuchsia-300',
        Q: 'bg-purple-300',
        R: 'bg-amber-300',
        S: 'bg-lime-300',
        T: 'bg-sky-300',
        U: 'bg-brown-300',
        V: 'bg-green-300',
        W: 'bg-blue-300',
        X: 'bg-pink-300',
        Y: 'bg-rose-300',
        Z: 'bg-cyan-300'
    };

    return colorMap[letter.toUpperCase()] || 'bg-gray-300'; // Default to gray if no specific color is defined
};

export const convertToCoverTypeEnumValue = (coverType: string): string => {
    const enumKey = Object.keys(TravelCoverType).find((key) => key === coverType);
    return enumKey ? TravelCoverType[enumKey as keyof typeof TravelCoverType] : 'Unknown Trip Type';
};

export const stringToTravelType = (value: string): TravelType => {
    const map: { [key: string]: TravelType } = {
        GROUP: TravelType.GROUP,
        FAMILY: TravelType.FAMILY,
        INDIVIDUAL: TravelType.INDIVIDUAL,
        STUDENT: TravelType.STUDENT,
        MULTIPLE_INDIVIDUAL: TravelType.MULTIPLE_INDIVIDUAL
    };

    return map[value.toUpperCase()];
};

declare global {
    interface String {
        toTitle(): string;
    }
}

String.prototype.toTitle = function (): string {
    return this.toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export const convertFromUSD = (amountInUSD: number, rate: number, toCurrency: string) =>
    `${addCommasToNumbers((amountInUSD * rate).toFixed(2).toString())} ${toCurrency}`;

export const roundAmountToWholeNumber = (displayedAmount: string): string => {
    const displayedAmountSplit = displayedAmount.split(' ');
    const amount = addCommasToNumbers(Number(displayedAmountSplit[0].replace(',', '')).toFixed(0));
    const currency = displayedAmountSplit[1];

    return amount.concat(` ${currency}`);
};

export const getAgeGroup = (dob: string): AgeGroup => {
    const today = dayjs();
    const birthDate = dayjs(dob, 'YYYY-MM-DD');
    const age = today.diff(birthDate, 'year');

    if (age < 18) {
        return AgeGroup.KENYA_UNDER_18;
    } else if (age >= 18 && age <= 69) {
        return AgeGroup.KENYA_18_TO_69;
    } else if (age >= 70 && age <= 75) {
        return AgeGroup.KENYA_70_TO_75;
    } else if (age >= 76 && age <= 80) {
        return AgeGroup.KENYA_76_TO_80;
    } else if (age >= 81 && age <= 85) {
        return AgeGroup.KENYA_81_TO_85;
    } else {
        throw new Error('Age is out of the defined range');
    }
};

export const getAgeFromDOB = (dob: string): number => {
    const today = dayjs();
    const birthDate = dayjs(dob, 'YYYY-MM-DD');
    return today.diff(birthDate, 'year');
};

export const filterNonNullable = <T>(items: T[]): T[] => {
    return items.filter((item): item is T => item != null);
};

export const getReadableFileKindName = (fileKind: FileKind) => {
    switch (fileKind) {
        case FileKind.FULL_TIME_EDUCATION:
            return 'Full Time Education';
        case FileKind.KRA:
            return 'KRA Pin Certificate';
        case FileKind.MEDICAL:
            return 'Medical Document';
        case FileKind.PASSPORT:
            return 'Passport';
        case FileKind.NATIONAL_ID:
            return 'National Id';
        default:
            throw new Error('File Kind not defined');
    }
};
// export const hashPassword = (password: string, saltRounds: number = 10): string => {
//     const salt = bcrypt.genSaltSync(saltRounds)
//     return bcrypt.hashSync(password, salt)
// };

export const mapUserRoleToHomeRoute = (userRole: UserRole): string => {
    switch (userRole) {
        case UserRole.ROLE_ADMINISTRATOR:
            return '/britam-admin/home';
        case UserRole.ROLE_AGENCY_ADMIN:
            return '/agency/home';
        case UserRole.ROLE_UNDER_WRITER:
            return '/britam-admin/home';
        case UserRole.ROLE_AGENT || UserRole.ROLE_BRANCH_MANAGER:
            return '/agency/home';
        case UserRole.ROLE_DIRECT_CLIENT:
            return '/travelportal/generate-quote';
    }
};

export const handleKYCTemplateDownload = async (
    under16Count: number,
    from16to70Count: number,
    groupCount?: number,
    from70to75Count: number = 0,
    from76to80Count: number = 0,
    from81to85Count: number = 0
) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Customer Details Template');

    worksheet.columns = [
        { header: 'Age Group', width: 25 },
        { header: 'Passport Number', width: 20 },
        { header: 'First Name', width: 20 },
        { header: 'Other Names', width: 20 },
        { header: 'Date of Birth', width: 18 },
        { header: 'Gender', width: 15 },
        { header: 'Nationality', width: 22 },
        { header: 'Phone Number', width: 20 },
        { header: 'Email Address', width: 25 },
        { header: 'KRA Pin Number', width: 20 },
        { header: 'Occupation', width: 20 }
    ];

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4472C4' }
    };

    const instRow = worksheet.addRow([
        '',
        '(text)',
        '(text)',
        '(text)',
        '(YYYY-MM-DD)',
        '(select)',
        '(text)',
        '(number)',
        '(text)',
        '(text)',
        '(text)'
    ]);
    instRow.font = { italic: true, color: { argb: 'FF7E7E7E' } };

    const styleRow = (row: ExcelJS.Row, isMain: boolean) => {
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            // For dependants: only cols 1-5 (Age Group, Passport, First Name, Other Names, DOB) require input.
            // Cols 6-7 (Gender, Nationality) are greyed — not collected for non-main travellers.
            const isRequiredForDependant = colNumber <= 5;
            const needsInput = isMain || isRequiredForDependant;

            if (needsInput) {
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            } else {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFF2F2F2' }
                };
            }

            // Gender dropdown only for main customer
            if (colNumber === 6 && isMain) {
                cell.dataValidation = {
                    type: 'list',
                    allowBlank: true,
                    formulae: ['"Male,Female"']
                };
            }
        });
    };

    const sections =
        groupCount !== undefined
            ? [
                  { label: 'Main Customer', count: 1, isMain: true },
                  { label: 'Age Group (18-85)', count: groupCount, isMain: false }
              ]
            : [
                  { label: 'Main Customer', count: 1, isMain: true },
                  { label: 'Age Group I (Under 18)', count: under16Count, isMain: false },
                  { label: 'Age Group II (18-69)', count: from16to70Count, isMain: false },
                  { label: 'Age Group III (70-75)', count: from70to75Count, isMain: false },
                  { label: 'Age Group IV (76-80)', count: from76to80Count, isMain: false },
                  { label: 'Age Group V (81-85)', count: from81to85Count, isMain: false }
              ];

    sections.forEach((sec) => {
        if (sec.count > 0) {
            const sectionHeader = worksheet.addRow([sec.label]);
            worksheet.mergeCells(sectionHeader.number, 1, sectionHeader.number, 11);

            sectionHeader.font = { bold: true, color: { argb: 'FF000000' } };
            sectionHeader.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFE9ECEF' }
            };

            for (let i = 0; i < sec.count; i++) {
                styleRow(worksheet.addRow(Array(11).fill('')), sec.isMain);
            }
        }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), 'KYC_Customer_Template.xlsx');
};

export interface ImportedKycData {
    mainCustomer: PersonDetails;
    dependantsUnder18: PersonDetails[];
    dependants18To69: PersonDetails[];
    dependantsGroup: PersonDetails[];
    dependants70To75: PersonDetails[];
    dependants76To80: PersonDetails[];
    dependants81To85: PersonDetails[];
}

export const extractKYCData = (
    file: File,
    under16Count: number,
    from16to70Count: number,
    groupCount?: number,
    from70to75Count: number = 0,
    from76to80Count: number = 0,
    from81to85Count: number = 0
): Promise<ImportedKycData> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsArrayBuffer(file);

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];

                // All rows as an array of arrays
                const allRows = XLSX.utils.sheet_to_json<any[]>(worksheet, { header: 1, defval: '' });

                const mapRowToPerson = (row: any[]): PersonDetails => ({
                    identificationNumber: row[1] || '',
                    firstName: row[2] || '',
                    otherNames: row[3] || '',
                    dateOfBirth: formatExcelDate(row[4]),
                    gender: row[5] || '',
                    nationality: row[6] || '',
                    phoneNumber: String(row[7] || ''),
                    emailAddress: row[8] || '',
                    taxNumber: row[9] || '',
                    occupation: row[10] || '',
                    kraFile: null,
                    passportFile: null,
                    medicalFile: null,
                    studentFile: null
                });

                const findHeaderIndex = (searchStr: string) => allRows.findIndex((row) => row[0]?.toString().includes(searchStr));

                const mainHeaderIdx = findHeaderIndex('Main Customer');
                const mainCustomer = mainHeaderIdx !== -1 ? mapRowToPerson(allRows[mainHeaderIdx + 1]) : null;

                const u16HeaderIdx = findHeaderIndex('Age Group I');
                const dependantsUnder18 =
                    u16HeaderIdx !== -1 ? allRows.slice(u16HeaderIdx + 1, u16HeaderIdx + 1 + under16Count).map(mapRowToPerson) : [];

                const from16HeaderIdx = findHeaderIndex('Age Group II');
                const dependants18To69 =
                    from16HeaderIdx !== -1
                        ? allRows.slice(from16HeaderIdx + 1, from16HeaderIdx + 1 + from16to70Count).map(mapRowToPerson)
                        : [];

                const groupHeaderIdx = findHeaderIndex('Age Group (18-85)');
                const dependantsGroup =
                    groupCount !== undefined && groupHeaderIdx !== -1
                        ? allRows.slice(groupHeaderIdx + 1, groupHeaderIdx + 1 + groupCount).map(mapRowToPerson)
                        : [];

                const ag3HeaderIdx = findHeaderIndex('Age Group III');
                const dependants70To75 =
                    from70to75Count > 0 && ag3HeaderIdx !== -1
                        ? allRows.slice(ag3HeaderIdx + 1, ag3HeaderIdx + 1 + from70to75Count).map(mapRowToPerson)
                        : [];

                const ag4HeaderIdx = findHeaderIndex('Age Group IV');
                const dependants76To80 =
                    from76to80Count > 0 && ag4HeaderIdx !== -1
                        ? allRows.slice(ag4HeaderIdx + 1, ag4HeaderIdx + 1 + from76to80Count).map(mapRowToPerson)
                        : [];

                const ag5HeaderIdx = findHeaderIndex('Age Group V');
                const dependants81To85 =
                    from81to85Count > 0 && ag5HeaderIdx !== -1
                        ? allRows.slice(ag5HeaderIdx + 1, ag5HeaderIdx + 1 + from81to85Count).map(mapRowToPerson)
                        : [];

                if (!mainCustomer) throw new Error('Main Customer data not found.');

                resolve({
                    mainCustomer,
                    dependantsUnder18,
                    dependants18To69,
                    dependantsGroup,
                    dependants70To75,
                    dependants76To80,
                    dependants81To85
                });
            } catch (error) {
                console.error('Extraction Error:', error);
                reject(error);
            }
        };
    });
};

const formatExcelDate = (val: any): string => {
    if (!val) return '';
    if (val instanceof Date) return val.toISOString().split('T')[0];
    if (typeof val === 'number') {
        // Excel stores dates as number of days since 1900
        const date = new Date(Math.round((val - 25569) * 86400 * 1000));
        return date.toISOString().split('T')[0];
    }
    return String(val);
};

export const exportToCSV = (name: string, data: any[]) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${name}.xlsx`);
};

export const convertSerialDateToDate = (excelSerialDate: string) =>
    dayjs(new Date(1900, 0, Number(excelSerialDate) - 1)).format('YYYY-MM-DD');

export const removeNameRole = (roleName: string): string => {
    let roles = roleName.split(' ');
    roles = roles.filter((name) => name !== 'Role');

    return roles.join(' ');
};

export const changeDateFormat = (updatedDate: string): string => {
    const date = new Date(updatedDate);
    const splitDate = date.toUTCString().split(' ');
    const filteredDate = splitDate.splice(0, 4);

    return filteredDate.join(' ');
};

// For checking if the exchange rate is valid (either whole number or 1 to 4 decimal places) or not.
export const checkIfInvalidRate = (rate: number): boolean => {
    const rateSplit = rate.toString().split('.');

    if (rateSplit.length === 1) {
        return false;
    }

    const decimalDigits = rateSplit[1].split('');
    return decimalDigits.length <= 4 ? false : true;
};

export const getKeyFromObjectValue = (object: Object, value: string | number) => {
    return Object.keys(object).find((key) => object[key] === value);
};

export const convertToEnumKey = (value: string) => value.toUpperCase().replace(/\s+/g, '_').replace(/\//g, '_OR_');

export const convertToEnumValue = (value: string) => {
    return value.replace(/_/g, ' ').toTitle();
};

const getIdentificationKeyValuePair = (): { key: string; value: string }[] => {
    const identificationKeys = Object.keys(IdentificationType);
    const identificationValues = Object.values(IdentificationType);

    const identificationTypeArray: {
        key: string;
        value: string;
    }[] = identificationKeys.map((key, index) => {
        return {
            key,
            value: identificationValues[index]
        };
    });

    return identificationTypeArray;
};

export const getIdentificationTypeKey = (value: string): string => {
    let key = '';
    const identificationTypeArray = getIdentificationKeyValuePair();

    for (let i = 0; i < identificationTypeArray.length; i++) {
        if (identificationTypeArray[i].value === value) {
            key = identificationTypeArray[i].key;
            break;
        }
    }

    return key;
};

export const getIdentificationTypeValue = (key: string): string => {
    let value = '';
    const identificationTypeArray = getIdentificationKeyValuePair();

    for (let i = 0; i < identificationTypeArray.length; i++) {
        if (identificationTypeArray[i].key === key) {
            value = identificationTypeArray[i].value;
            break;
        }
    }

    return value;
};

// For checking if phone number input has the plus sign ONLY.
const checkPlusSignOnly = (phoneNumber: string): boolean => {
    const ifPlusOnly = false;
    const splitPhoneNumber = phoneNumber.split('');

    if (splitPhoneNumber.length > 1) {
        return ifPlusOnly;
    }

    return splitPhoneNumber[0] === '+';
};

const removePhoneNumberSpaces = (phoneNumber: string): string => {
    let phoneSplit = phoneNumber.split('');
    phoneSplit = phoneSplit.filter((string) => string !== ' ');

    return phoneSplit.join('');
};

// Check whether a Kenyan number starts with '7' or '1' after the number code.
const checkValidKenyanNumber = (phoneNumber: string): boolean => {
    const validKenyanNumber = true;

    const kenyanCodeRegex = /^\+254/;
    // Ignore non-kenyan numbers
    if (kenyanCodeRegex.test(phoneNumber) === false) {
        return validKenyanNumber;
    }

    const validKenyanNumberRegex = /^\+254(7|1)/;
    return validKenyanNumberRegex.test(phoneNumber);
};

export const validatePhoneNumber = (phoneNumber: string): boolean => {
    let validPhoneNumber = true;

    // Return validPhoneNumber as false since phoneNumber as "+" only is not a phone number.
    if (checkPlusSignOnly(phoneNumber)) {
        validPhoneNumber = false;
        return validPhoneNumber;
    }

    const phoneNumberWithoutSpaces = removePhoneNumberSpaces(phoneNumber);

    const numberRegex = /^\+25(0|4)/;
    // Validate kenyan or rwandan numbers only (To be checked for other countries later).
    if (numberRegex.test(phoneNumberWithoutSpaces) === false) {
        return validPhoneNumber;
    }

    // Return validPhoneNumber as false when Kenyan number does not start with '7' or '1' after the country code.
    if (checkValidKenyanNumber(phoneNumberWithoutSpaces) === false) {
        validPhoneNumber = false;
        return validPhoneNumber;
    }

    const splitPhoneNumber = phoneNumberWithoutSpaces.split(numberRegex);
    validPhoneNumber = splitPhoneNumber[splitPhoneNumber.length - 1].length === 9;

    return validPhoneNumber;
};

export const getUserCountryObject = (country: string): { id: number; label: string; flag: string } => {
    const countries = [
        { label: 'KE', id: 1, flag: Kenya },
        { label: 'RW', id: 2, flag: Rwanda }
    ];

    if (country === 'Kenya') {
        return countries[0];
    } else {
        return countries[1];
    }
};

export const getCorporateNameInitials = (corporateName: string): string => {
    let initials = '';

    const corporateNameSplit = corporateName.split(' ');
    // If there's only one name, return the name's first letter.
    if (corporateNameSplit.length === 1) {
        initials = corporateNameSplit[0].split('')[0].toUpperCase();
        return initials;
    }

    for (const name of corporateNameSplit) {
        initials += name.split('')[0].toUpperCase();
    }

    return initials;
};

export const concatenateDestinations = (destinations: QuoteDestination[]): string => {
    let concatenatedDestinations = '';

    for (let index = 0; index < destinations.length; index++) {
        const destinationName = destinations[index].destinationName;
        const separator = index === destinations.length - 1 ? '' : ', ';

        concatenatedDestinations += `${destinationName}${separator}`;
    }

    return concatenatedDestinations;
};

export const concatenateLocalTravelDestinations = (values: string[]): string => {
    let concatenatedDestinations = '';

    if (values === null || values.length === 0) return concatenatedDestinations;

    const destinationSplit = values[0].split(',');

    if (destinationSplit.length === 1) {
        concatenatedDestinations = destinationSplit[0].toTitle();
    } else {
        for (let i = 0; i < destinationSplit.length; i++) {
            const separator = i === destinationSplit.length - 1 ? '' : ', ';
            concatenatedDestinations += `${destinationSplit[i].toTitle()}${separator}`;
        }
    }

    return concatenatedDestinations;
};
